import HorizontalDivider from '@/global/components/Divider/HorizontalDivider'
import { MarkdownView } from '@/global/components/Markdown/MarkdownView'
import Grid from '@/global/components/ui/Grid'
import Dialog from '@/global/guides/Dialog'
import { hasPermission } from '@/hooks/useBackendMenu'
import { formatarNumero } from '@/lib/utils'
import { useCamapanhaOutletContext } from '@/pages/Campanhas.context'
import { CategoriaEnum } from '@acme/auth'
import dayjs from 'dayjs'
import {
    FormDatePicker,
    FormField,
    FormFieldAutocomplete,
    FormFieldCheckbox,
    FormFieldSelect,
} from 'global/components/Form'
import {
    BellRingIcon,
    MessageSquareTextIcon,
    PictureInPictureIcon,
} from 'lucide-react'
import { useParams } from 'react-router-dom'
import { Campanha, Cliente, clientes as clientesApi } from 'service/api'
import { PaginatedResponse } from 'service/api/types'
import { rotuloStatus } from '../status'

/** Carrega a lista inteira: o alvo pode citar qualquer cliente, não só a 1ª página. */
const TODOS_OS_CLIENTES = { perPage: Number.MAX_SAFE_INTEGER }

const ROTULO_DA_SEVERIDADE = {
    info: 'Informação',
    success: 'Sucesso',
    warning: 'Atenção',
    error: 'Erro',
}

function toDatetimeLocal(data?: string) {
    return data ? dayjs(data).format('YYYY-MM-DDTHH:mm') : ''
}

function toDatetimeDiplay(data?: string) {
    return data ? dayjs(data).format('DD/MM/YYYY HH:mm') : ''
}

/**
 * Traduz a campanha para os mesmos campos do formulário de publicação, que é
 * quem dá o formato: o alvo vira texto porque é assim que o select compara os
 * valores, e o curinga esvazia a lista, já que não vira select.
 */
export function valoresCampanha(campanha: Campanha) {
    const { alvo, botao } = campanha

    return {
        situacao: rotuloStatus(campanha.status),
        titulo: campanha.titulo,
        mensagem: campanha.mensagem,
        severity: campanha.severity,
        corpoMarkdown: campanha.corpoMarkdown ?? '',
        envio: campanha.umaVez ? 'Uma vez' : 'Até ler',
        inicio: toDatetimeLocal(campanha.inicio),
        fim: toDatetimeLocal(campanha.fim),
        categorias:
            alvo.categorias === 'todas' ? [] : alvo.categorias.map(String),
        clienteIds:
            alvo.clienteIds === 'todos' ? [] : alvo.clienteIds.map(String),
        categoriasTexto:
            alvo.categorias === 'todas' ? 'Todas as categorias' : '',
        clientesTexto: alvo.clienteIds === 'todos' ? 'Todos os clientes' : '',
        botaoLabel: botao?.label ?? '',
        botaoUrl: botao?.url ?? '',
        leitores: formatarNumero(campanha.leitores),
        autor: campanha.autor?.nome ?? '',
        criadaEm: toDatetimeDiplay(campanha.createdAt),
        encerradaEm: toDatetimeDiplay(campanha.encerradaEm),
        _modo_badge: campanha.modos.includes('badge'),
        _modo_toast: campanha.modos.includes('toast'),
        _modo_modal: campanha.modos.includes('modal'),
    }
}

export default function DetalhesCampanha() {
    const { id } = useParams()
    const { data: lista = [] } = useCamapanhaOutletContext()
    const campanha = lista.find(item => item.id === id)

    const { data: clientes } = clientesApi.useFetch<PaginatedResponse<Cliente>>(
        {
            enabled: Boolean(campanha) && campanha?.alvo.clienteIds !== 'todos',
            queryParams: TODOS_OS_CLIENTES,
        },
    )

    if (!campanha) return null

    const { alvo, botao, modos } = campanha
    const isToastBadge = modos.includes('toast') || modos.includes('badge')

    const opcoesCategorias =
        alvo.categorias === 'todas'
            ? []
            : alvo.categorias.map(categoria => ({
                  value: String(categoria),
                  label: CategoriaEnum[categoria] ?? `#${categoria}`,
              }))

    const opcoesClientes =
        alvo.clienteIds === 'todos'
            ? []
            : alvo.clienteIds.map(clienteId => ({
                  value: String(clienteId),
                  label:
                      (clientes?.data ?? []).find(
                          cliente => cliente.id === clienteId,
                      )?.nomeFantasia ?? `#${clienteId}`,
              }))

    return (
        <Dialog.Root
            open={hasPermission(['read', 'Notificações'])}
            disableFields
            defaultValues={valoresCampanha(campanha)}
        >
            <Dialog.Title>Detalhes da notificação</Dialog.Title>
            <Dialog.Content>
                <Grid container>
                    <Grid item xs={12} sm={6}>
                        <FormField name="titulo" label="Título" />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <FormField name="situacao" label="Situação" />
                    </Grid>
                    <Grid item xs={12} className="my-4">
                        <HorizontalDivider>Modos</HorizontalDivider>
                    </Grid>
                    {modos.includes('badge') && (
                        <Grid item xs={12} sm={4}>
                            <FormFieldCheckbox
                                name="_modo_badge"
                                label={
                                    <span className="inline-flex gap-2">
                                        Central
                                        <BellRingIcon />
                                    </span>
                                }
                            />
                        </Grid>
                    )}
                    {modos.includes('toast') && (
                        <Grid item xs={12} sm={4}>
                            <FormFieldCheckbox
                                name="_modo_toast"
                                label={
                                    <span className="inline-flex gap-2">
                                        Toast
                                        <MessageSquareTextIcon />
                                    </span>
                                }
                            />
                        </Grid>
                    )}
                    {modos.includes('modal') && (
                        <Grid item xs={12} sm={4}>
                            <FormFieldCheckbox
                                name="_modo_modal"
                                label={
                                    <span className="inline-flex gap-2">
                                        Diálogo
                                        <PictureInPictureIcon />
                                    </span>
                                }
                            />
                        </Grid>
                    )}
                    {isToastBadge && (
                        <>
                            <Grid item xs={12} className="my-4">
                                <HorizontalDivider>
                                    Configurações de Toast/Central
                                </HorizontalDivider>
                            </Grid>
                            <Grid item xs={12}>
                                <FormFieldSelect
                                    name="severity"
                                    label="Severidade"
                                    options={Object.entries(
                                        ROTULO_DA_SEVERIDADE,
                                    ).map(([value, label]) => ({
                                        value,
                                        label,
                                    }))}
                                />
                            </Grid>
                            {campanha.mensagem && (
                                <Grid item xs={12}>
                                    <FormField
                                        name="mensagem"
                                        label="Mensagem"
                                        multiline
                                        rows={2}
                                    />
                                </Grid>
                            )}
                        </>
                    )}
                    {campanha.corpoMarkdown && (
                        <>
                            <Grid item xs={12} className="my-4">
                                <HorizontalDivider>
                                    Corpo do diálogo
                                </HorizontalDivider>
                            </Grid>
                            <Grid
                                item
                                xs={12}
                                className="border rounded-md p-1 shadow-sm"
                            >
                                <MarkdownView campanhaId={campanha.id}>
                                    {campanha.corpoMarkdown}
                                </MarkdownView>
                            </Grid>
                        </>
                    )}
                    {botao && (
                        <>
                            <Grid item xs={12} className="my-4">
                                <HorizontalDivider>
                                    Botão do diálogo
                                </HorizontalDivider>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <FormField
                                    name="botaoLabel"
                                    label="Rótulo do botão"
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <FormField
                                    name="botaoUrl"
                                    label="URL do botão"
                                />
                            </Grid>
                        </>
                    )}
                    <Grid item xs={12} className="my-4">
                        <HorizontalDivider>
                            Configurações de entrega
                        </HorizontalDivider>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <FormField name="envio" label="Envio" />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <FormField name="leitores" label="Leram" />
                    </Grid>
                    {campanha.inicio && (
                        <Grid item xs={12} sm={6}>
                            <FormDatePicker
                                name="inicio"
                                label="Início"
                                type="datetime-local"
                            />
                        </Grid>
                    )}
                    {campanha.fim && (
                        <Grid item xs={12} sm={6}>
                            <FormDatePicker
                                name="fim"
                                label="Fim"
                                type="datetime-local"
                            />
                        </Grid>
                    )}
                    {alvo.categorias === 'todas' ? (
                        <Grid item xs={12}>
                            <FormField
                                name="categoriasTexto"
                                label="Entregar para as categorias"
                            />
                        </Grid>
                    ) : (
                        <Grid item xs={12}>
                            <FormFieldAutocomplete
                                name="categorias"
                                label="Entregar para as categorias"
                                options={opcoesCategorias}
                                getOptionLabel={opcao => opcao.label}
                                paraCampo={(valores: string[] = []) =>
                                    opcoesCategorias.filter(opcao =>
                                        valores.includes(opcao.value),
                                    )
                                }
                                multiple
                            />
                        </Grid>
                    )}
                    {alvo.clienteIds === 'todos' ? (
                        <Grid item xs={12}>
                            <FormField
                                name="clientesTexto"
                                label="Entregar para os clientes"
                            />
                        </Grid>
                    ) : (
                        <Grid item xs={12}>
                            <FormFieldAutocomplete
                                name="clienteIds"
                                label="Entregar para os clientes"
                                options={opcoesClientes}
                                getOptionLabel={opcao => opcao.label}
                                paraCampo={(valores: string[] = []) =>
                                    opcoesClientes.filter(opcao =>
                                        valores.includes(opcao.value),
                                    )
                                }
                                multiple
                            />
                        </Grid>
                    )}
                    <Grid item xs={12} className="my-4">
                        <HorizontalDivider>Publicação</HorizontalDivider>
                    </Grid>
                    {campanha.autor && (
                        <Grid item xs={12} sm={6}>
                            <FormField name="autor" label="Publicada por" />
                        </Grid>
                    )}
                    <Grid item xs={12} sm={6}>
                        <FormField name="criadaEm" label="Criada em" />
                    </Grid>
                    {campanha.encerradaEm && (
                        <Grid item xs={12} sm={6}>
                            <FormField
                                name="encerradaEm"
                                label="Encerrada em"
                            />
                        </Grid>
                    )}
                </Grid>
            </Dialog.Content>
            <Dialog.Actions>
                <Dialog.Actions.SecondaryButton />
            </Dialog.Actions>
        </Dialog.Root>
    )
}
